<template>
  <b-form-group
    :class="`forms-image-uploader ${validationStateClass} ${tiny ? 'tiny' : ''}`"
    :label="label"
    :label-for="field"
    :description="description"
  >
    <div v-if="loading">
      <layout-loading :with-button="tiny" />
    </div>
    <div v-else-if="!value" :class="{ 'mb-3': !tiny }">
      <b-form-file
        :id="field"
        :ref="`${field}imageinput`"
        :value="value"
        :state="validationState"
        :placeholder="placeholder"
        :disabled="disabled"
        :name="field"
        :accept="accept.join(',')"
        :browse-text="$t('components.forms.image_uploader.select')"
        :drop-placeholder="$t('components.forms.image_uploader.drop_placeholder')"
        @change="handleChange"
      />
      <div v-if="error" class="invalid-feedback">
        {{ error }}
      </div>
    </div>

    <div v-if="value && !loading" :class="{ 'button-list': tiny }">
      <figure v-if="!tiny" class="preview">
        <safe-image
          :image="value"
          :aspect-ratio="previewAspectRatio"
          :alt="alt || $t('components.forms.image_uploader.preview')"
        />
      </figure>
      <safe-image v-else :image="value" link :alt="alt" />
      <icon-button v-if="!disabled" role="remove-item" @click="removeImage">
        {{ tiny ? "" : removeImageText }}
      </icon-button>
    </div>
  </b-form-group>
</template>

<script>
import IconButton from "@/components/shared/IconButton.vue";
import SafeImage from "@/components/shared/SafeImage.vue";
import { capitalize } from "@/helpers/filters";
import { uploadImage } from "@/requests/fileRequests";

export default {
  name: "FormsImageUploader",
  components: { IconButton, SafeImage },
  props: {
    accept: {
      default: () => ["*.png", "*.jpg", "*.jpeg", "image/png", "image/jpg", "image/jpeg"],
      type: Array,
    },
    description: {
      type: String,
      required: false,
      default: "",
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    field: {
      required: true,
      type: String,
    },
    label: {
      required: false,
      type: String,
      default: "",
    },
    placeholder: {
      default: function () {
        return capitalize(this.$t("forms.upload"));
      },
      type: String,
    },
    removeImageText: {
      default: function () {
        return this.$t("components.forms.image_uploader.remove_image");
      },
      type: String,
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    state: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    value: {
      type: Object,
      require: false,
      default: undefined,
    },
    previewAspectRatio: {
      type: String,
      required: false,
      default: undefined,
    },
    alt: {
      type: String,
      default: undefined,
    },
    tiny: {
      type: Boolean,
      default: false,
    },
  },
  data: function () {
    return {
      loading: false,
      error: null,
    };
  },
  computed: {
    validationState() {
      return !this.error && ((this.required && !!this.value) || this.state);
    },
    validationStateClass() {
      switch (this.validationState) {
        case true:
          return "is-valid";
        case false:
          return "is-invalid";
        default:
          return "";
      }
    },
  },
  watch: {
    loading() {
      this.$emit("loading", this.loading);
    },
  },
  methods: {
    handleChange(event) {
      switch (event.type) {
        case "drop":
          this.uploadImage(event.dataTransfer.files);
          break;
        default:
          this.uploadImage(event.target.files);
          break;
      }
    },
    removeImage() {
      this.$emit("input", null);

      if (this.$refs[`${this.field}imageinput`]) {
        this.$refs[`${this.field}imageinput`].reset();
      }
    },
    async uploadImage(fileList) {
      this.loading = true;
      const { file, error } = await uploadImage(this.field, fileList, {
        order: this.value?.length ?? 0,
      });
      this.loading = false;
      if (file) {
        this.$emit("input", file);
      }
      this.error = error;
    },
  },
};
</script>

<style lang="scss">
.forms-image-uploader.tiny .custom-file-label::after {
  display: none;
}

.forms-image-uploader:not(.tiny) {
  .custom-file {
    min-height: 180px;
  }

  .custom-file-label {
    overflow: hidden;
    height: 180px;
    text-align: center;

    border: 1px dashed $light-grey;

    background-image: url("/cloud.svg");
    background-repeat: no-repeat;
    background-size: auto 50%;
    background-position: top 40px center;

    &.dragging {
      background-image: url("/cloud-active.svg");
      border: 1px dashed $primary;
    }

    &::after {
      border-left: 0;
      border-radius: 0.25rem;
      position: absolute;
      bottom: 0.5rem;
      left: 0;
      right: 0;
      top: auto;
      width: max(50%, 200px);
      max-width: 100%;
      margin: 0 auto;
    }
  }

  + .invalid-feedback {
    margin-top: -20px;
  }
}
</style>
